import {
  FREE_EMAIL_AUDIT_API_PATH,
  FREE_EMAIL_AUDIT_ERROR_CODES,
  type FreeEmailAuditErrorCode,
  type FreeEmailAuditReport,
  type FreeEmailAuditRequest,
} from '@/lib/free-tools/email-audit/contracts'

/**
 * Why an audit request failed: the route's own `code`, or one the page
 * names itself (no answer, or an answer it cannot read). The page words it
 * in the reader's language (`emailAuditCopy(t).error`).
 */
type EmailAuditRequestError = FreeEmailAuditErrorCode | 'network' | 'unreadable'

export type EmailAuditRequestResult =
  | { ok: true; report: FreeEmailAuditReport }
  | { ok: false; error: EmailAuditRequestError }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isErrorCode(value: unknown): value is FreeEmailAuditErrorCode {
  return (FREE_EMAIL_AUDIT_ERROR_CODES as ReadonlyArray<unknown>).includes(
    value
  )
}

/**
 * Read the route's JSON without trusting its shape: a proxy or platform
 * error can answer with any body, and none of them may throw here.
 */
function readResult(payload: unknown): EmailAuditRequestResult {
  if (isRecord(payload) && isRecord(payload.report)) {
    return { ok: true, report: payload.report as FreeEmailAuditReport }
  }
  const code =
    isRecord(payload) && isRecord(payload.error)
      ? payload.error.code
      : undefined
  return { ok: false, error: isErrorCode(code) ? code : 'unreadable' }
}

/** POST the free audit from the page. Every failure comes back as a code. */
export async function requestFreeEmailAudit(
  body: FreeEmailAuditRequest
): Promise<EmailAuditRequestResult> {
  let response: Response
  try {
    response = await fetch(FREE_EMAIL_AUDIT_API_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    return { ok: false, error: 'network' }
  }
  const payload: unknown = await response.json().catch(() => null)
  return readResult(payload)
}
